import {
  byRenderOrder,
  defaultPlacement,
  parsePlacement,
  placeSections,
  ProductList,
  ProductListProps,
  type GridPlacementConfig,
  type PlacementInput,
} from "@made-people/blueprint-product-list";

import type { SbBlokData } from "@storyblok/js";
import { useMemo } from "react";
import { ProductListContentCell } from "./ProductListContentCell";

interface ProductListContentBlockBlok extends SbBlokData {
  content?: SbBlokData[];
  placement?: string | object;
  aspectMobile?: string;
  aspectDesktop?: string;
}

const MISS_MARY_GRID: GridPlacementConfig = {
  columns: { mobile: 2, desktop: 4 },
  visibilityClass: { mobile: "lg:hidden", desktop: "hidden lg:grid" },
  productAspect: { width: 5, height: 7 },
};

const toPlacementInput = (
  block: ProductListContentBlockBlok,
): PlacementInput | undefined => {
  if ((block.content ?? []).length === 0) {
    return undefined;
  }

  return {
    placement: parsePlacement(block.placement) ?? defaultPlacement(),
    aspect: { mobile: block.aspectMobile, desktop: block.aspectDesktop },
  };
};

export const ProductListWithContent = (
  props: ProductListProps & { contentBlocks?: ProductListContentBlockBlok[] },
) => {
  const { contentBlocks, ...productListProps } = props;

  const contentSections = useMemo(() => {
    if (!contentBlocks) {
      return undefined;
    }

    const toSpanNumber = (span: unknown, fallback: number) =>
      typeof span !== "string" || span === "full"
        ? fallback
        : Number(span) || fallback;

    return placeSections(contentBlocks.map(toPlacementInput), MISS_MARY_GRID)
      .sort(byRenderOrder)
      .map((cell) => ({
        position: cell.position,
        columnSpan: cell.columnSpan,
        rowSpan: cell.rowSpan,
        className: cell.className ?? "",
        content: (
          <ProductListContentCell
            components={contentBlocks[cell.sectionIndex].content ?? []}
            metrics={cell.metrics}
            spannedColumns={toSpanNumber(
              cell.columnSpan,
              MISS_MARY_GRID.columns.desktop,
            )}
            spannedRows={toSpanNumber(cell.rowSpan, 1)}
          />
        ),
      }));
  }, [contentBlocks]);

  return (
    <ProductList
      {...productListProps}
      contentSections={contentSections}
      contentSectionsSettings={{
        ...productListProps.contentSectionsSettings,
        hideWhenFiltered: true,
      }}
    />
  );
};
